import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { saveLegalSurvey, checkDuplicateLegalAddress } from './dynamodb';

function LegalSurvey() {
  const [formData, setFormData] = useState({
    address: '',
    email: '',
    supportLeaving: '',
    supportLegalRep: '',
    willingToPay: '',
    comments: '',
    surveyType: 'legal'
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIntro, setShowIntro] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      setErrorMessage('Please complete the CAPTCHA verification.');
      setModalType('error');
      setShowModal(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const isDuplicate = await checkDuplicateLegalAddress(formData.address);
      if (isDuplicate) {
        setErrorMessage('This address has already submitted this survey. Each household may only submit once.');
        setModalType('error');
        setShowModal(true);
        return;
      }

      await saveLegalSurvey({ ...formData });
      setModalType('success');
      setShowModal(true);
      setFormData({
        address: '',
        email: '',
        supportLeaving: '',
        supportLegalRep: '',
        willingToPay: '',
        comments: '',
        surveyType: 'legal'
      });
      setCaptchaValue(null);
    } catch (error) {
      setErrorMessage('There was an error submitting your survey. Please try again.');
      setModalType('error');
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="survey-container">
      <div className="survey-pattern"></div>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10 col-12">
            <div className="modern-form p-5 fade-in">
              <div className="text-center">
                <div className="survey-icon mx-auto">⚖️</div>
                <h1 className="survey-title">Legal Action Survey</h1>
                <p className="survey-subtitle">
                  Help us understand resident support for pursuing legal representation to leave FirstPort
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Address */}
                <div className="mb-4">
                  <label className="form-label">House number and street name: *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 15 Meadow Bank"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </div>

                {/* Email address */}
                <div className="mb-4">
                  <label className="form-label">Email address: *</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="e.g. resident@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                  <div className="form-text text-muted">
                    A confirmation of your responses will be sent to this address.
                  </div>
                </div>

                {/* Support leaving FirstPort */}
                <div className="mb-4">
                  <label className="form-label">
                    Would you support pursuing the process of leaving FirstPort as our management company? *
                  </label>
                  <select
                    className="form-select"
                    value={formData.supportLeaving}
                    onChange={(e) => setFormData({ ...formData, supportLeaving: e.target.value })}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes, I support this</option>
                    <option value="no">No, I do not support this</option>
                    <option value="unsure">Unsure / Need more information</option>
                  </select>
                </div>

                {/* Support using legal representation */}
                <div className="mb-4">
                  <label className="form-label">
                    Would you support using legal representation to assist with the process of leaving FirstPort? *
                  </label>
                  <select
                    className="form-select"
                    value={formData.supportLegalRep}
                    onChange={(e) => setFormData({ ...formData, supportLegalRep: e.target.value })}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes, I support using legal representation</option>
                    <option value="no">No, I do not support this</option>
                    <option value="unsure">Unsure / Need more information</option>
                  </select>
                </div>

                {/* Willing to contribute to legal costs */}
                <div className="mb-4">
                  <label className="form-label">
                    Legal costs are estimated at <strong>£1,500 – £2,500</strong> in total, shared across
                    participating households. Would you be willing to contribute a small fee to cover these costs? *
                  </label>
                  <select
                    className="form-select"
                    value={formData.willingToPay}
                    onChange={(e) => setFormData({ ...formData, willingToPay: e.target.value })}
                    required
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes, I am willing to contribute</option>
                    <option value="no">No, I am not willing to contribute</option>
                    <option value="unsure">Unsure / Depends on the amount</option>
                  </select>
                </div>

                {/* Optional comments */}
                <div className="mb-4">
                  <label className="form-label">Additional comments: (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Any questions, concerns, or thoughts you'd like to share..."
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  ></textarea>
                </div>

                <div className="mb-4 d-flex justify-content-center">
                  <ReCAPTCHA
                    sitekey="6Ldifl0pAAAAADZCvzJSAm-wBdsmx0flhew4zXhT"
                    onChange={setCaptchaValue}
                    size="compact"
                  />
                </div>

                <div className="text-center mt-4">
                  <button type="submit" className="btn btn-survey" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit Survey'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Intro modal */}
      {showIntro && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="col-lg-6 col-md-8 col-10">
            <div className="modern-form p-5" style={{ backgroundColor: '#dc3545', color: '#ffd700' }}>
              <div className="text-center">
                <h1 className="survey-title" style={{ color: '#ffd700' }}>Important Information</h1>
              </div>
              <div className="mb-4">
                <p className="mb-3">
                  The residents' committee is exploring whether to formally pursue leaving FirstPort using
                  legal representation. Before taking any action, we want to understand the level of support
                  across the estate.
                </p>
                <p className="mb-3">
                  Legal costs for this process are estimated at <strong style={{ color: '#fff' }}>£1,500 – £2,500</strong> in
                  total. If enough residents are willing to contribute, this cost would be shared and kept
                  as low as possible per household.
                </p>
                <p className="mb-0">
                  Your responses are confidential and will only be used to gauge overall resident support.
                  No action will be taken without a clear majority.
                </p>
              </div>
              <div className="text-center">
                <button
                  className="btn"
                  style={{ backgroundColor: 'white', color: '#dc3545', border: 'none', padding: '10px 20px', borderRadius: '5px' }}
                  onClick={() => setShowIntro(false)}
                >
                  Continue to Survey
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success / error modal */}
      {showModal && (
        <div className="success-modal" onClick={() => setShowModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className={`success-icon ${modalType === 'error' ? 'error-icon' : ''}`}>
              {modalType === 'success' ? '✓' : '⚠'}
            </div>
            <h3 className="mb-3">
              {modalType === 'success' ? 'Survey Submitted!' : 'Submission Error'}
            </h3>
            <p className="text-muted mb-4">
              {modalType === 'success'
                ? 'Thank you for your response. Your feedback has been recorded and a confirmation has been sent to your email address.'
                : errorMessage || 'There was an error submitting your survey. Please try again.'}
            </p>
            <button className="btn btn-survey" onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LegalSurvey;
